import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Tag,
  Modal,
  Dropdown,
  Button,
  Input,
  Table,
  Select,
  Card,
  Tooltip,
  message,
  Tabs,
} from "antd";
import {
  FiMoreVertical,
  FiFilter,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiFlag,
  FiStar,
  FiEye,
  FiLayers,
} from "react-icons/fi";
import {
  RiEyeFill,
  RiDeleteBin6Line,
  RiCheckDoubleLine,
  RiCloseLine,
  RiStarFill,
  RiStarLine,
  RiFlagLine,
} from "react-icons/ri";
import {
  getAllPromptsPaginated,
  getPromptsAnalyticsOverview,
  getAllPromptCategories,
  approvePrompt,
  rejectPrompt,
  deletePromptById,
  featurePrompt,
  unfeaturePrompt,
  clearPromptFlags,
  bulkApprovePrompts,
  bulkRejectPrompts,
} from "@api/prompts";
import { useHistory } from "react-router-dom";
import { SuccessNotification, ErrorNotification } from "@components";
import "@assets/css/AdminList.css";

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

// Animated number component
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = value || 0;
    if (end === 0) {
      setDisplayValue(0);
      return;
    }
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{displayValue.toLocaleString()}</span>;
};

const PromptsList = () => {
  const history = useHistory();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Filters
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterVisibility, setFilterVisibility] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterFeatured, setFilterFeatured] = useState(null);
  const [categories, setCategories] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    totalPrompts: 0,
    pendingReview: 0,
    flaggedPrompts: 0,
    featuredPrompts: 0,
    approvedPrompts: 0,
    rejectedPrompts: 0,
    publicPrompts: 0,
    privatePrompts: 0,
  });

  // Modals
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [featureModalVisible, setFeatureModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [featureNote, setFeatureNote] = useState("");
  const [isStaffPick, setIsStaffPick] = useState(false);

  // Bulk actions
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkRejectModalVisible, setBulkRejectModalVisible] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  // Current tab
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchPrompts();
    fetchStats();
    fetchCategories();
  }, []);

  const fetchPrompts = async (
    page = 1,
    perPage = 20,
    filters = {}
  ) => {
    setLoading(true);
    try {
      const res = await getAllPromptsPaginated(page, perPage, {
        status: filters.status || filterStatus,
        visibility: filters.visibility || filterVisibility,
        category: filters.category || filterCategory,
        featured: filters.featured || filterFeatured,
        search: filters.search !== undefined ? filters.search : searchText,
      });

      setPrompts(res.data.prompts);
      setPagination({
        current: res.data.page,
        pageSize: res.data.perPage,
        total: res.data.total,
      });
    } catch (error) {
      console.error("Fetch prompts error", error);
      message.error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getPromptsAnalyticsOverview();
      setStats({
        totalPrompts: res.data.totalPrompts || 0,
        pendingReview: res.data.pendingReview || 0,
        flaggedPrompts: res.data.flaggedPrompts || 0,
        featuredPrompts: res.data.featuredPrompts || 0,
        approvedPrompts: res.data.approvedPrompts || 0,
        rejectedPrompts: res.data.rejectedPrompts || 0,
        publicPrompts: res.data.publicPrompts || 0,
        privatePrompts: res.data.privatePrompts || 0,
      });
    } catch (error) {
      console.error("Fetch stats error", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getAllPromptCategories(true);
      setCategories(res.data || []);
    } catch (error) {
      console.error("Fetch categories error", error);
    }
  };

  // Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchPrompts(1, pagination.pageSize, { search: value });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedRowKeys([]);

    let filters = {};
    if (key === "pending") {
      filters.status = "pending_review";
    } else if (key === "flagged") {
      // Flagged prompts will be fetched differently
    } else if (key === "featured") {
      filters.featured = "true";
    }

    setFilterStatus(filters.status || null);
    setFilterFeatured(filters.featured || null);
    fetchPrompts(1, pagination.pageSize, filters);
  };

  const handleApprove = async (promptId) => {
    try {
      await approvePrompt(promptId);
      SuccessNotification("Prompt approved successfully");
      fetchPrompts(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to approve prompt");
    }
  };

  const handleReject = async () => {
    if (!selectedPrompt || !rejectReason) return;

    try {
      await rejectPrompt(selectedPrompt._id, rejectReason);
      SuccessNotification("Prompt rejected successfully");
      setRejectModalVisible(false);
      setSelectedPrompt(null);
      setRejectReason("");
      fetchPrompts(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to reject prompt");
    }
  };

  const handleFeature = async () => {
    if (!selectedPrompt) return;

    try {
      await featurePrompt(selectedPrompt._id, {
        editorNote: featureNote,
        staffPick: isStaffPick,
      });
      SuccessNotification("Prompt featured successfully");
      setFeatureModalVisible(false);
      setSelectedPrompt(null);
      setFeatureNote("");
      setIsStaffPick(false);
      fetchPrompts(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to feature prompt");
    }
  };

  const handleUnfeature = async (promptId) => {
    try {
      await unfeaturePrompt(promptId);
      SuccessNotification("Prompt unfeatured successfully");
      fetchPrompts(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to unfeature prompt");
    }
  };

  const handleClearFlags = async (promptId) => {
    Modal.confirm({
      title: "Clear All Flags",
      content: "Are you sure you want to clear all flags for this prompt?",
      okText: "Clear Flags",
      okType: "primary",
      onOk: async () => {
        try {
          await clearPromptFlags(promptId);
          SuccessNotification("Flags cleared successfully");
          fetchPrompts(pagination.current, pagination.pageSize);
          fetchStats();
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Failed to clear flags");
        }
      },
    });
  };

  const handleDelete = (promptId, promptTitle) => {
    Modal.confirm({
      title: "Delete Prompt",
      content: (
        <div>
          <p>Are you sure you want to delete <strong>{promptTitle}</strong>?</p>
          <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
            This will also remove all related reviews, saves, and collection entries.
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await deletePromptById(promptId);
          SuccessNotification("Prompt deleted successfully");
          fetchPrompts(pagination.current, pagination.pageSize);
          fetchStats();
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Failed to delete prompt");
        }
      },
    });
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select prompts to approve");
      return;
    }

    Modal.confirm({
      title: `Approve ${selectedRowKeys.length} prompts?`,
      content: "This will approve all selected prompts.",
      okText: "Approve All",
      okType: "primary",
      onOk: async () => {
        try {
          await bulkApprovePrompts(selectedRowKeys);
          SuccessNotification(`${selectedRowKeys.length} prompts approved`);
          setSelectedRowKeys([]);
          fetchPrompts(pagination.current, pagination.pageSize);
          fetchStats();
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Failed to bulk approve");
        }
      },
    });
  };

  const handleBulkReject = async () => {
    if (!bulkRejectReason) return;

    try {
      await bulkRejectPrompts(selectedRowKeys, bulkRejectReason);
      SuccessNotification(`${selectedRowKeys.length} prompts rejected`);
      setBulkRejectModalVisible(false);
      setBulkRejectReason("");
      setSelectedRowKeys([]);
      fetchPrompts(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to bulk reject");
    }
  };

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending_review: { color: "orange", icon: <FiClock size={12} />, label: "Pending" },
      approved: { color: "green", icon: <FiCheckCircle size={12} />, label: "Approved" },
      rejected: { color: "red", icon: <RiCloseLine size={12} />, label: "Rejected" },
      draft: { color: "default", icon: <FiEye size={12} />, label: "Draft" },
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <Tag className={`admin-status-tag ${status === "approved" ? "active" : status === "rejected" ? "disabled" : "pending"}`}>
        {config.icon}
        {config.label}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Prompt",
      key: "prompt",
      width: 320,
      render: (_, record) => (
        <div className="admin-user-info">
          <div
            className="admin-user-avatar"
            style={{
              background: record.category?.color || "#7c3aed",
              fontSize: "14px"
            }}
          >
            {(record.title || record.name || "P").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div
              className="admin-user-name"
              onClick={() => {
                setSelectedPrompt(record);
                setViewModalVisible(true);
              }}
              style={{ cursor: "pointer" }}
            >
              {record.title || record.name}
              {record.featured?.isFeatured && (
                <RiStarFill size={14} color="#f59e0b" style={{ marginLeft: 6 }} />
              )}
            </div>
            <div className="admin-user-email">
              {record.category?.name || "Uncategorized"}
              {record.author && ` • by ${record.author.firstName || record.author.email}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: ["moderation", "status"],
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      width: 100,
      render: (visibility) => (
        <Tag color={visibility === "public" ? "blue" : "default"}>
          {visibility === "public" ? "Public" : "Private"}
        </Tag>
      ),
    },
    {
      title: "Flags",
      key: "flags",
      width: 80,
      render: (_, record) => {
        const flagCount = record.moderation?.flagCount || 0;
        if (flagCount === 0) return <span style={{ color: "#94a3b8" }}>-</span>;
        return (
          <Tooltip title={`${flagCount} flags reported`}>
            <Tag color="red" icon={<FiFlag size={12} />}>
              {flagCount}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Uses",
      dataIndex: "usageCount",
      key: "usageCount",
      width: 80,
      render: (count) => (
        <span style={{ fontWeight: 600 }}>{(count || 0).toLocaleString()}</span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (
        <span style={{ color: "#64748b", fontSize: "13px" }}>
          {formatDate(date)}
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <div className="admin-dropdown-menu">
              <button
                className="admin-dropdown-item"
                onClick={() => {
                  setSelectedPrompt(record);
                  setViewModalVisible(true);
                }}
              >
                <RiEyeFill size={15} />
                View Details
              </button>
              <div className="admin-dropdown-divider" />

              {record.moderation?.status === "pending_review" && (
                <>
                  <button
                    className="admin-dropdown-item success"
                    onClick={() => handleApprove(record._id)}
                  >
                    <RiCheckDoubleLine size={15} />
                    Approve
                  </button>
                  <button
                    className="admin-dropdown-item warning"
                    onClick={() => {
                      setSelectedPrompt(record);
                      setRejectModalVisible(true);
                    }}
                  >
                    <RiCloseLine size={15} />
                    Reject
                  </button>
                  <div className="admin-dropdown-divider" />
                </>
              )}

              {record.featured?.isFeatured ? (
                <button
                  className="admin-dropdown-item"
                  onClick={() => handleUnfeature(record._id)}
                >
                  <RiStarLine size={15} />
                  Remove Featured
                </button>
              ) : (
                <button
                  className="admin-dropdown-item"
                  onClick={() => {
                    setSelectedPrompt(record);
                    setFeatureModalVisible(true);
                  }}
                >
                  <RiStarFill size={15} />
                  Feature Prompt
                </button>
              )}

              {(record.moderation?.flagCount || 0) > 0 && (
                <button
                  className="admin-dropdown-item"
                  onClick={() => handleClearFlags(record._id)}
                >
                  <RiFlagLine size={15} />
                  Clear Flags
                </button>
              )}

              <div className="admin-dropdown-divider" />
              <button
                className="admin-dropdown-item danger"
                onClick={() => handleDelete(record._id, record.title || record.name)}
              >
                <RiDeleteBin6Line size={15} />
                Delete Prompt
              </button>
            </div>
          }
          placement="bottomRight"
        >
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const handleTableChange = (pag) => {
    setPagination({ ...pagination, current: pag.current, pageSize: pag.pageSize });
    fetchPrompts(pag.current, pag.pageSize);
  };

  const resetFilters = () => {
    setFilterStatus(null);
    setFilterVisibility(null);
    setFilterCategory(null);
    setFilterFeatured(null);
    setSearchText("");
    setPagination({ ...pagination, current: 1 });
    fetchPrompts(1, pagination.pageSize, {
      status: null,
      visibility: null,
      category: null,
      featured: null,
      search: "",
    });
  };

  const handleFilter = () => {
    setPagination({ ...pagination, current: 1 });
    fetchPrompts(1, pagination.pageSize);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const filterMenu = (
    <div className="admin-filter-dropdown">
      <div className="admin-filter-section">
        <div className="admin-filter-label">Status</div>
        <Select
          style={{ width: "100%" }}
          value={filterStatus}
          onChange={setFilterStatus}
          allowClear
          placeholder="All Status"
        >
          <Option value="pending_review">Pending Review</Option>
          <Option value="approved">Approved</Option>
          <Option value="rejected">Rejected</Option>
          <Option value="draft">Draft</Option>
        </Select>
      </div>
      <div className="admin-filter-section">
        <div className="admin-filter-label">Visibility</div>
        <Select
          style={{ width: "100%" }}
          value={filterVisibility}
          onChange={setFilterVisibility}
          allowClear
          placeholder="All Visibility"
        >
          <Option value="public">Public</Option>
          <Option value="private">Private</Option>
          <Option value="unlisted">Unlisted</Option>
        </Select>
      </div>
      <div className="admin-filter-section">
        <div className="admin-filter-label">Category</div>
        <Select
          style={{ width: "100%" }}
          value={filterCategory}
          onChange={setFilterCategory}
          allowClear
          placeholder="All Categories"
        >
          {categories.map((cat) => (
            <Option key={cat._id} value={cat._id}>
              {cat.name}
            </Option>
          ))}
        </Select>
      </div>
      <div className="admin-filter-section">
        <div className="admin-filter-label">Featured</div>
        <Select
          style={{ width: "100%" }}
          value={filterFeatured}
          onChange={setFilterFeatured}
          allowClear
          placeholder="All"
        >
          <Option value="true">Featured Only</Option>
          <Option value="false">Not Featured</Option>
        </Select>
      </div>
      <div className="admin-filter-actions">
        <Button className="admin-btn-secondary" onClick={resetFilters}>
          Reset
        </Button>
        <Button className="admin-btn-primary" onClick={handleFilter}>
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <div className="admin-list-container">
      {/* Header */}
      <div className="admin-list-header">
        <div>
          <h1>Prompts</h1>
          <p>Manage all prompts, moderation, and featured content</p>
        </div>
        <div className="admin-list-actions">
          <Button
            className="admin-btn-secondary"
            icon={<FiRefreshCw size={16} />}
            onClick={() => {
              fetchPrompts(pagination.current, pagination.pageSize);
              fetchStats();
            }}
          >
            Refresh
          </Button>
          <Button
            className="admin-btn-secondary purple"
            icon={<FiLayers size={16} />}
            onClick={() => history.push("/super-admin/prompts/categories")}
          >
            Categories
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[20, 20]} className="admin-stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon blue">
              <FiLayers />
            </div>
            <div className="admin-stat-label">Total Prompts</div>
            <div className="admin-stat-value">
              <AnimatedNumber value={stats.totalPrompts} />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon orange">
              <FiClock />
            </div>
            <div className="admin-stat-label">Pending Review</div>
            <div className="admin-stat-value orange">
              <AnimatedNumber value={stats.pendingReview} />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon red">
              <FiAlertTriangle />
            </div>
            <div className="admin-stat-label">Flagged</div>
            <div className="admin-stat-value red">
              <AnimatedNumber value={stats.flaggedPrompts} />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="admin-stat-card">
            <div className="admin-stat-icon purple">
              <FiStar />
            </div>
            <div className="admin-stat-label">Featured</div>
            <div className="admin-stat-value purple">
              <AnimatedNumber value={stats.featuredPrompts} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: 16 }}>
        <TabPane tab="All Prompts" key="all" />
        <TabPane
          tab={
            <span>
              Pending Review
              {stats.pendingReview > 0 && (
                <Tag color="orange" style={{ marginLeft: 8 }}>{stats.pendingReview}</Tag>
              )}
            </span>
          }
          key="pending"
        />
        <TabPane
          tab={
            <span>
              Flagged
              {stats.flaggedPrompts > 0 && (
                <Tag color="red" style={{ marginLeft: 8 }}>{stats.flaggedPrompts}</Tag>
              )}
            </span>
          }
          key="flagged"
        />
        <TabPane tab="Featured" key="featured" />
      </Tabs>

      {/* Action Bar */}
      <div className="admin-action-bar">
        <div className="admin-search-wrapper">
          <Input
            className="admin-search-input"
            placeholder="Search prompts by title or description..."
            prefix={<FiSearch size={16} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => handleSearch(searchText)}
            allowClear
            onClear={() => handleSearch("")}
          />
        </div>
        <div className="admin-list-actions">
          {selectedRowKeys.length > 0 && (
            <>
              <Button
                className="admin-btn-secondary"
                icon={<RiCheckDoubleLine size={16} />}
                onClick={handleBulkApprove}
              >
                Approve ({selectedRowKeys.length})
              </Button>
              <Button
                className="admin-btn-secondary"
                icon={<RiCloseLine size={16} />}
                onClick={() => setBulkRejectModalVisible(true)}
              >
                Reject ({selectedRowKeys.length})
              </Button>
            </>
          )}
          <Dropdown
            overlay={filterMenu}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button className="admin-btn-secondary" icon={<FiFilter size={16} />}>
              Filters
              {(filterStatus || filterVisibility || filterCategory || filterFeatured) && (
                <Tag color="purple" style={{ marginLeft: 8, marginRight: 0 }}>
                  {[filterStatus, filterVisibility, filterCategory, filterFeatured].filter(Boolean).length}
                </Tag>
              )}
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Table */}
      <Card className="admin-table-card">
        <Table
          className="admin-table"
          columns={columns}
          dataSource={prompts}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} prompts`,
          }}
          loading={loading}
          onChange={handleTableChange}
          rowKey={(record) => record._id}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* View Prompt Modal */}
      <Modal
        className="admin-modal"
        title="Prompt Details"
        visible={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedPrompt(null);
        }}
        footer={null}
        width={700}
      >
        {selectedPrompt && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8 }}>{selectedPrompt.title || selectedPrompt.name}</h3>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {getStatusTag(selectedPrompt.moderation?.status)}
                <Tag color={selectedPrompt.visibility === "public" ? "blue" : "default"}>
                  {selectedPrompt.visibility}
                </Tag>
                {selectedPrompt.featured?.isFeatured && (
                  <Tag color="gold" icon={<RiStarFill size={12} />}>Featured</Tag>
                )}
                {selectedPrompt.category && (
                  <Tag>{selectedPrompt.category.name}</Tag>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Description:</strong>
              <p style={{ color: "#64748b", marginTop: 4 }}>
                {selectedPrompt.description || "No description provided"}
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Prompt Content:</strong>
              <div
                style={{
                  background: "#f8fafc",
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 4,
                  whiteSpace: "pre-wrap",
                  maxHeight: 300,
                  overflow: "auto"
                }}
              >
                {selectedPrompt.content || selectedPrompt.prompt || "No content"}
              </div>
            </div>

            <Row gutter={16}>
              <Col span={8}>
                <div style={{ color: "#64748b" }}>Author</div>
                <div>{selectedPrompt.author?.firstName || selectedPrompt.author?.email || "Unknown"}</div>
              </Col>
              <Col span={8}>
                <div style={{ color: "#64748b" }}>Created</div>
                <div>{formatDate(selectedPrompt.createdAt)}</div>
              </Col>
              <Col span={8}>
                <div style={{ color: "#64748b" }}>Usage Count</div>
                <div>{(selectedPrompt.usageCount || 0).toLocaleString()}</div>
              </Col>
            </Row>

            {selectedPrompt.moderation?.rejectionReason && (
              <div style={{ marginTop: 16, padding: 12, background: "#fef2f2", borderRadius: 8 }}>
                <strong style={{ color: "#dc2626" }}>Rejection Reason:</strong>
                <p style={{ color: "#dc2626", marginTop: 4 }}>
                  {selectedPrompt.moderation.rejectionReason}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        className="admin-modal"
        title="Reject Prompt"
        visible={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setSelectedPrompt(null);
          setRejectReason("");
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-btn-secondary"
            onClick={() => {
              setRejectModalVisible(false);
              setSelectedPrompt(null);
              setRejectReason("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-btn-primary"
            danger
            disabled={!rejectReason}
            onClick={handleReject}
          >
            Reject Prompt
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>
          Rejecting: <strong>{selectedPrompt?.title || selectedPrompt?.name}</strong>
        </p>
        <TextArea
          rows={4}
          placeholder="Enter rejection reason (will be visible to the author)..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* Feature Modal */}
      <Modal
        className="admin-modal"
        title="Feature Prompt"
        visible={featureModalVisible}
        onCancel={() => {
          setFeatureModalVisible(false);
          setSelectedPrompt(null);
          setFeatureNote("");
          setIsStaffPick(false);
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-btn-secondary"
            onClick={() => {
              setFeatureModalVisible(false);
              setSelectedPrompt(null);
              setFeatureNote("");
              setIsStaffPick(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-btn-primary"
            onClick={handleFeature}
          >
            Feature Prompt
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>
          Featuring: <strong>{selectedPrompt?.title || selectedPrompt?.name}</strong>
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Editor's Note (optional)</label>
          <TextArea
            rows={3}
            placeholder="Add a note about why this prompt is featured..."
            value={featureNote}
            onChange={(e) => setFeatureNote(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isStaffPick}
              onChange={(e) => setIsStaffPick(e.target.checked)}
            />
            Mark as Staff Pick
          </label>
        </div>
      </Modal>

      {/* Bulk Reject Modal */}
      <Modal
        className="admin-modal"
        title={`Reject ${selectedRowKeys.length} Prompts`}
        visible={bulkRejectModalVisible}
        onCancel={() => {
          setBulkRejectModalVisible(false);
          setBulkRejectReason("");
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-btn-secondary"
            onClick={() => {
              setBulkRejectModalVisible(false);
              setBulkRejectReason("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-btn-primary"
            danger
            disabled={!bulkRejectReason}
            onClick={handleBulkReject}
          >
            Reject All
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>
          This will reject {selectedRowKeys.length} selected prompts.
        </p>
        <TextArea
          rows={4}
          placeholder="Enter rejection reason for all selected prompts..."
          value={bulkRejectReason}
          onChange={(e) => setBulkRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default PromptsList;
